import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import {ProspectPlayersReportAPI} from "@/api/prospect_players_reports";

import AddIcon from "@mui/icons-material/Add";

import {dateFormat} from "@/utils/constants";
import {getAlertSeverity} from "@/utils/functions";

import "./index.scss";

const fieldDateFormat = "YYYY-MM-DD";

const defaultFiltersData = {
	toDate: moment().endOf("month").format(fieldDateFormat),
};

function ProspectPlayersReportsPage(props) {
	const {expanded = true, playerId} = props;

	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [valuations, setValuations] = useState([]);

	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("prospect_player_report_filters")) ||
			defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"prospect_player_report_filters",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData?.fromDate
				? moment(filtersData?.fromDate).format(fieldDateFormat)
				: undefined,
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						fromDate: moment(e).format(fieldDateFormat),
					});
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.toDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					setFiltersData({
						...filtersData,
						toDate: moment(e).format(fieldDateFormat),
					});
				}
			},
		},
	];

	const getValuations = async () => {
		setLoading(true);
		try {
			const response = playerId
				? await ProspectPlayersReportAPI.listByPlayerId(playerId)
				: await ProspectPlayersReportAPI.list(filtersData);
			setValuations(response);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getValuations();
	}, [filtersData, playerId]);

	const mainColumns = [
		{
			label: "Nome",
			key: "player.firstName",
			align: "start",
			sortable: true,
		},
		{
			label: "Cognome",
			key: "player.lastName",
			align: "start",
			sortable: true,
			sortedByDefault: true,
			sortedOrderByDefault: "ascend",
		},
		{
			label: "Data di nascita",
			key: "player.dateOfBirth",
			type: "date",
			customRender: (item) => {
				return item?.player?.dateOfBirth
					? moment(item?.player?.dateOfBirth).format(dateFormat)
					: "---";
			},
			medium: true,
			sortable: true,
		},
		{
			label: "Ruolo",
			key: "role",
			customRender: (item) => {
				return item?.player?.goalkeeper ? "P" : item?.player?.primaryRole?.[0];
			},
			small: true,
			align: "start",
		},
		{
			label: "N° valutazioni",
			key: "total",
			customRender: (item) => {
				return item?.children?.length;
			},
			align: "end",
			small: true,
		},
	];

	const columns = [
		{
			label: "Data",
			key: "valuationDate",
			type: "date",
			customRender: (item, rows) => {
				return moment(item.valuationDate).format(dateFormat);
			},
			small: true,
			sortable: true,
			sortedByDefault: true,
			sortedOrderByDefault: "descend",
		},
		{
			label: "Allenatore",
			key: "signalerName",
			align: "start",
			medium: true,
		},

		{
			label: "Squadra",
			key: "club",
			customRender: (item) => {
				return item.club?.name;
			},
			medium: true,
			align: "start",
		},
		{
			label: "Categoria",
			key: "playerCategory",
			customRender: (item) => {
				return item.playerCategory?.name;
			},
			small: true,
			align: "start",
		},
		{
			label: "Esito",
			key: "judgment",
			customRender: (item) => {
				const alertData = getAlertSeverity(item.judgment, false) || {
					severity: "",
					message: "",
				};
				return (
					<div className={"severity " + (alertData.severity || "")}>
						{alertData.message || "---"}
					</div>
				);
			},
			small: true,
		},
		{
			label: "Voto finale",
			key: "overallRating",
			customRender: (item) => {
				const rating = parseFloat(item.overallRating);
				return isNaN(rating) ? "---" : rating.toFixed(0);
			},
			align: "end",
			small: true,
		},
	];

	// raggruppamento delle valutazioni per id del giocatore
	const handleReports = (data) => {
		const newReports = [];
		data.forEach((r) => {
			if (newReports.filter((nr) => nr.id === r.playerId)?.length) {
				newReports.map((nr) => {
					if (nr.id === r.playerId) {
						nr.children.push(r);
					}
					return nr;
				});
			} else {
				newReports.push({
					id: r.playerId,
					player: r.player,
					children: [r],
				});
			}
		});

		return newReports;
	};

	const listConfiguration = {
		data: valuations,
		onClick: (valuation) =>
			navigate("/scouting/prospect-players-reports/" + valuation.id),
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			onClick: () => navigate("/scouting/prospect-players-reports/new"),
		},
	];

	const pageList = (
		<CardsList
			type="valuation"
			breadcrumbs={!playerId}
			tabbed={playerId}
			fitHeight
			filtersData={!playerId ? filters : undefined}
			listConfiguration={listConfiguration}
			sortOptions={columns.filter((c) => c.sortable)}
		/>
	);

	const getChildRows = (parentRow) => {
		return parentRow.children || [];
	};

	const pageBody = (
		<CardsContainer
			tableComponent={
				<CardsTable
					fixExpanded={false}
					configurationColumns={expanded ? mainColumns : columns}
					configurationExpandedColumns={expanded ? columns : undefined}
					sortOptions={!playerId && mainColumns.filter((c) => c.sortable)}
					data={expanded ? handleReports(valuations) : valuations}
					breadcrumbs={!playerId}
					fullWidth
					fullHeight={!playerId}
					filtersData={!playerId ? filters : undefined}
					handleClick={(valuation) => {
						navigate("/scouting/prospect-players-reports/" + valuation.id);
					}}
					defaultSortedField="valuationDate"
					defaultSortedOrder="descend"
					rowGroupingKey={expanded ? "id" : undefined}
					getChildRows={expanded ? getChildRows : undefined}
				/>
			}
			listComponent={pageList}
		/>
	);

	if (playerId) {
		return pageBody;
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs actions={<MainActions actions={mainActions} />} />
			{pageBody}
		</Page>
	);
}

export default ProspectPlayersReportsPage;
